import React, { useState, useEffect, useRef } from 'react';

export default function InlineField({
  value, onSave, canEdit, mono, multiline,
  tabGroup, tabIndex, placeholder,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');
  const [local, setLocal]     = useState(value ?? '');
  const inputRef  = useRef(null);
  const confirmed = useRef(false);
  // Long-press (mobile): mantener presionado un campo lo pone en edición.
  // Tap corto en mobile no edita (deja burbujear para expandir la tarjeta).
  const pressTimer = useRef(null);
  const pressStart = useRef(null);
  const longFired  = useRef(false);
  const isMobile = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

  function onPointerDown(e) {
    if (!canEdit || !isMobile()) return;
    longFired.current = false;
    pressStart.current = { x: e.clientX, y: e.clientY };
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => { longFired.current = true; startEdit(); }, 450);
  }
  function onPointerMove(e) {
    if (!pressStart.current) return;
    if (Math.abs(e.clientX - pressStart.current.x) > 10 || Math.abs(e.clientY - pressStart.current.y) > 10) {
      clearTimeout(pressTimer.current);
    }
  }
  function endPress() { clearTimeout(pressTimer.current); pressStart.current = null; }

  useEffect(() => {
    if (!editing) setLocal(value ?? '');
  }, [value, editing]);

  useEffect(() => {
    if (tabGroup && tabIndex !== undefined) {
      tabGroup.current[tabIndex] = startEdit;
    }
  });

  function startEdit() {
    if (!canEdit || editing) return;
    confirmed.current = false;
    setDraft(local);
    setEditing(true);
  }

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
      // En mobile: subir el campo por encima del teclado virtual una vez que abrió.
      if (window.matchMedia('(max-width: 768px)').matches) {
        setTimeout(() => {
          inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 320);
      }
    }
  }, [editing]);

  async function confirm() {
    if (confirmed.current) return;
    confirmed.current = true;
    setEditing(false);
    const newVal = draft.trim();
    if (newVal === local) return;
    const prev = local;
    setLocal(newVal);
    try { await onSave(newVal); }
    catch { setLocal(prev); }
  }

  function cancel() {
    confirmed.current = true;
    setEditing(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      confirm();
      const next = e.shiftKey ? tabIndex - 1 : tabIndex + 1;
      tabGroup?.current[next]?.();
    } else if (e.key === 'Enter') {
      if (multiline && e.ctrlKey) {
        e.preventDefault();
        const ta = inputRef.current;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        setDraft(d => d.slice(0, start) + '\n' + d.slice(end));
        requestAnimationFrame(() => { if (ta) { ta.selectionStart = ta.selectionEnd = start + 1; } });
        return;
      }
      e.preventDefault();
      confirm();
    }
  }

  if (editing) {
    const cls = `inline-input${mono ? ' inline-mono' : ''}${multiline ? ' inline-textarea' : ''}`;
    if (multiline) {
      return (
        <textarea
          ref={inputRef}
          className={cls}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={confirm}
          placeholder={placeholder || ''}
          rows={3}
        />
      );
    }
    return (
      <input
        ref={inputRef}
        className={cls}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={confirm}
        placeholder={placeholder || ''}
      />
    );
  }

  return (
    <span
      className={`inline-trigger${canEdit ? ' editable' : ''}`}
      onClick={canEdit ? (e => {
        // Desktop: click edita. Mobile: solo edita si fue long-press (y frena
        // el toggle de la tarjeta); el tap corto burbujea para expandir.
        if (isMobile()) {
          if (longFired.current) { e.stopPropagation(); longFired.current = false; }
          return;
        }
        startEdit();
      }) : undefined}
      onPointerDown={canEdit ? onPointerDown : undefined}
      onPointerMove={canEdit ? onPointerMove : undefined}
      onPointerUp={canEdit ? endPress : undefined}
      onPointerLeave={canEdit ? endPress : undefined}
      onContextMenu={canEdit ? (e => { if (isMobile()) e.preventDefault(); }) : undefined}
      title={local || undefined}
    >
      {local || <span style={{ color: 'var(--ag-dim)' }}>{placeholder || '—'}</span>}
    </span>
  );
}
